import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Selectors
import { selectors as userSelectors } from 'store/modules/user';

// Dependencies
import * as Sentry from '@sentry/react';

// components
import ErrorComponent from './components/ErrorComponent';
import { CONNECTIVITY_ERROR, DEFAULT_ERROR } from './components/ErrorComponent/constants';

const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  /loading chunk/i,
  /chunkloaderror/i,
  /importing a module script failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
];

const getErrorMessage = error => (error?.message || '').trim();

const isDynamicImportError = error =>
  DYNAMIC_IMPORT_ERROR_PATTERNS.some(pattern => pattern.test(getErrorMessage(error)));

const getReloadStorageKey = () => {
  const release =
    import.meta.env.VITE_VERSION || import.meta.env.REACT_APP_VERSION || 'unknown-release';

  return `dynamic-import-reload:${release}:${window.location.pathname}`;
};

const reloadWithCacheBust = () => {
  const currentUrl = new URL(window.location.href);

  currentUrl.searchParams.set('refresh', Math.random().toString(36).slice(2, 12));
  window.location.replace(currentUrl.toString());
};

const withErrorHandler = Component => {
  class WithErrorHandler extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        error: null,
        isConnectivityError: false,
      };
    }

    componentDidCatch(error, errorInfo) {
      const { userData } = this.props;
      const isConnectivityError = isDynamicImportError(error);
      const reloadStorageKey = getReloadStorageKey();
      const hasRetriedChunkReload = window.sessionStorage.getItem(reloadStorageKey) === '1';

      if (isConnectivityError && !hasRetriedChunkReload) {
        window.sessionStorage.setItem(reloadStorageKey, '1');
        reloadWithCacheBust();
        return;
      }

      // Setting the state
      this.setState({
        error,
        isConnectivityError,
      });
      // Sending error to sentry
      if (!isConnectivityError) {
        Sentry.withScope(scope => {
          Object.keys(errorInfo).forEach(key => {
            scope.setExtra(key, errorInfo[key]);
            scope.setUser({
              id: userData.get('id'),
              email: userData.get('username'),
            });
          });
          Sentry.captureException(error);
        });
      }
    }

    render() {
      const { error, isConnectivityError } = this.state;

      return error ? (
        <ErrorComponent errorType={isConnectivityError ? CONNECTIVITY_ERROR : DEFAULT_ERROR} />
      ) : (
        <Component {...this.props} />
      );
    }
  }

  WithErrorHandler.propTypes = {
    userData: PropTypes.instanceOf(Object).isRequired,
  };

  const mapStateToProps = state => ({
    userData: userSelectors.getUser(state),
  });

  WithErrorHandler.displayName = `withErrorHandler(${Component.displayName})`;
  return connect(mapStateToProps)(WithErrorHandler);
};

export default withErrorHandler;
